import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { ghs, num, formatDate } from "../lib/format";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, LineChart, Line, Legend } from "recharts";
import { Info, CheckCircle, Warning, ArrowCounterClockwise, X, DownloadSimple, Gauge } from "@phosphor-icons/react";
import RouteReplayModal from "../components/RouteReplayModal";
import ExportButton from "../components/ExportButton";
import { TripCharts } from "../components/TripCharts";

const TABS = [
  { key: "by-job",        label: "By Client",       labelKey: "job",            testid: "report-tab-client" },
  { key: "by-vehicle",    label: "By Truck",        labelKey: "vehicle_number", testid: "report-tab-vehicle" },
  { key: "by-driver",     label: "By Driver",       labelKey: "driver_name",    testid: "report-tab-driver" },
  { key: "active-trucks", label: "Active Trucks",   labelKey: "vehicle_number", testid: "report-tab-active",     isActiveTrucks: true },
  { key: "cost-eff",      label: "Cost & Efficiency", labelKey: "vehicle_number", testid: "report-tab-cost-eff", isCostEff: true },
  { key: "anomalies",     label: "Fuel Anomalies",  labelKey: "vehicle_number", testid: "report-tab-anomalies",  isAnomalies: true },
];

export default function Reports() {
  const [params] = useSearchParams();
  const initialTab = params.get("tab");
  const validTabKeys = new Set(TABS.map((t) => t.key));
  const [tab, setTab] = useState(validTabKeys.has(initialTab) ? initialTab : "by-job");
  const [month, setMonth] = useState("");
  const [days, setDays] = useState(30);
  const [thresholdPct, setThresholdPct] = useState(30);
  const [anomDays, setAnomDays] = useState(90);
  const [includeReviewed, setIncludeReviewed] = useState(false);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  const active = TABS.find((t) => t.key === tab);

  useEffect(() => {
    if (active.isCostEff) return; // CostEfficiency manages its own data
    let alive = true;
    setLoading(true);
    let url; let params2;
    if (active.isActiveTrucks) { url = `/reports/${tab}`; params2 = { days }; }
    else if (active.isAnomalies) { url = "/anomalies"; params2 = { threshold_pct: thresholdPct, days: anomDays, limit: 500, include_reviewed: includeReviewed }; }
    else { url = `/reports/${tab}`; params2 = month ? { month } : {}; }

    api.get(url, { params: params2 }).then(({ data }) => {
      if (!alive) return;
      if (active.isAnomalies) { setRows(data.items || []); setMeta(data); }
      else { setRows(data); setMeta(null); }
    }).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [tab, month, days, thresholdPct, anomDays, includeReviewed, refreshTick, active.isActiveTrucks, active.isAnomalies, active.isCostEff]);

  if (active.isAnomalies) {
    return <Anomalies
      rows={rows} meta={meta} loading={loading}
      thresholdPct={thresholdPct} setThresholdPct={setThresholdPct}
      anomDays={anomDays} setAnomDays={setAnomDays}
      includeReviewed={includeReviewed} setIncludeReviewed={setIncludeReviewed}
      tabs={TABS} tab={tab} setTab={setTab}
      onReviewed={() => setRefreshTick((x) => x + 1)} />;
  }
  if (active.isActiveTrucks) {
    return <ActiveTrucks rows={rows} loading={loading} days={days} setDays={setDays} tabs={TABS} tab={tab} setTab={setTab} />;
  }
  if (active.isCostEff) {
    return <CostEfficiency tabs={TABS} tab={tab} setTab={setTab} month={month} setMonth={setMonth} />;
  }

  const top10 = rows.slice(0, 10).map((r) => ({ ...r, name: r[active.labelKey] || "—" }));
  const grandLitres = rows.reduce((s, r) => s + (r.total_litres || r.litres || 0), 0);
  const grandYard = rows.reduce((s, r) => s + (r.yard_litres || 0), 0);
  const grandTopup = rows.reduce((s, r) => s + (r.topup_litres || 0), 0);
  const grandRevenue = rows.reduce((s, r) => s + (r.revenue_ghs || r.cost_ghs || 0), 0);

  return (
    <div data-testid="reports-page">
      <PageHeader subtitle="Analytics" title="Reports" />

      <ReportTabs tabs={TABS} tab={tab} setTab={setTab} month={month} setMonth={setMonth}>
        <div className="flex items-end">
          <ExportDispensesButton month={month} />
        </div>
        <div className="ml-auto flex flex-wrap gap-x-8 gap-y-2">
          <Stat label="Yard litres" value={num(grandYard)} accent="text-ps-blue" />
          <Stat label="Top-up litres" value={num(grandTopup)} accent="text-ps-orange" />
          <Stat label="Total litres" value={num(grandLitres)} accent="text-ps-deep" />
          <Stat label="Revenue" value={ghs(grandRevenue)} accent="text-lime-700" />
        </div>
      </ReportTabs>

      <div className="p-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-6 card-lift">
          <div className="text-[11px] uppercase tracking-[0.2em] text-ps-blue font-semibold mb-1">Top 10</div>
          <h3 className="font-display font-extrabold text-xl tracking-tight text-ps-deep mb-4">{active.label} — revenue</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%" minHeight={300} minWidth={200} debounce={50}>
              <BarChart data={top10} layout="vertical" margin={{ left: 90, right: 16, top: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fontFamily: "JetBrains Mono", fill: "#475569" }} stroke="#CBD5E1" tickLine={false} axisLine={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fontFamily: "JetBrains Mono", fill: "#475569" }} stroke="#CBD5E1" tickLine={false} axisLine={false} width={90} />
                <Tooltip contentStyle={{ border: "1px solid #CBD5E1", borderRadius: 6, fontFamily: "JetBrains Mono", fontSize: 12 }} cursor={{ fill: "rgba(0,111,158,0.05)" }} formatter={(v) => ghs(v)} />
                <Bar dataKey="revenue_ghs" name="Revenue" fill="#006f9e" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200">
            <div className="text-[11px] uppercase tracking-[0.2em] text-ps-blue font-semibold">Full breakdown</div>
            <h3 className="font-display font-extrabold text-xl tracking-tight text-ps-deep">{active.label}</h3>
          </div>
          <div className="overflow-auto max-h-[28rem]">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.2em] text-slate-600 border-b border-slate-200 sticky top-0">
                <tr>
                  <th className="px-3 py-3 text-left">{active.label.replace("By ", "")}</th>
                  <th className="px-3 py-3 text-right">Yard&nbsp;L</th>
                  <th className="px-3 py-3 text-right">Top-up&nbsp;L</th>
                  <th className="px-3 py-3 text-right">Total&nbsp;L</th>
                  <th className="px-3 py-3 text-right">Revenue</th>
                  <th className="px-3 py-3 text-right">Trips</th>
                  {tab === "by-job" && <th className="px-3 py-3 text-right">Trucks</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-testid="report-table-body">
                {loading && <tr><td colSpan={7} className="p-6"><div className="brand-progress w-1/3" /></td></tr>}
                {!loading && rows.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-500">No data.</td></tr>}
                {rows.map((r, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-semibold text-ps-deep">{r[active.labelKey] || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono-data tabular text-ps-blue">{num(r.yard_litres ?? r.litres)}</td>
                    <td className="px-3 py-2 text-right font-mono-data tabular text-ps-orange">{r.topup_litres ? num(r.topup_litres) : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono-data tabular">{num(r.total_litres ?? r.litres)}</td>
                    <td className="px-3 py-2 text-right font-mono-data tabular font-semibold text-lime-700">{ghs(r.revenue_ghs ?? r.cost_ghs)}</td>
                    <td className="px-3 py-2 text-right font-mono-data tabular">{r.trips}</td>
                    {tab === "by-job" && (
                      <td className="px-3 py-2 text-right">
                        <span className="px-2 py-0.5 text-[11px] font-bold rounded-md bg-sky-50 text-ps-blue" title={(r.truck_list || []).join(", ")}>
                          {r.trucks_used || 0}
                        </span>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <TripCharts />
    </div>
  );
}

const SEVERITY_STYLES = {
  high:   { badge: "bg-red-50 text-ps-red border-red-200", bar: "bg-ps-red", label: "HIGH" },
  medium: { badge: "bg-orange-50 text-ps-orange border-orange-200", bar: "bg-ps-orange", label: "MED" },
  low:    { badge: "bg-amber-50 text-amber-700 border-amber-200", bar: "bg-amber-400", label: "LOW" },
};

const STATUS_BADGE = {
  legitimate:   { cls: "bg-lime-50 text-lime-700 border-lime-200", label: "Legitimate" },
  investigated: { cls: "bg-orange-50 text-ps-orange border-orange-200", label: "Investigated" },
};

function Anomalies({ rows, meta, loading, thresholdPct, setThresholdPct, anomDays, setAnomDays, includeReviewed, setIncludeReviewed, tabs, tab, setTab, onReviewed }) {
  const [reviewModal, setReviewModal] = useState(null); // { row, status }
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const openReview = (row, status) => { setReviewModal({ row, status }); setNote(""); };

  const submitReview = async () => {
    if (!reviewModal) return;
    setSaving(true);
    try {
      await api.patch(`/dispenses/${reviewModal.row.id}/anomaly`, { status: reviewModal.status, note });
      setReviewModal(null);
      onReviewed?.();
    } catch (e) {
      alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const reopenRow = async (row) => {
    try {
      await api.patch(`/dispenses/${row.id}/anomaly`, { status: "pending", note: "" });
      onReviewed?.();
    } catch (e) {
      alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div data-testid="reports-page">
      <PageHeader subtitle="Analytics" title="Reports" />

      <div className="px-8 py-4 bg-slate-100 border-b border-slate-200 flex flex-wrap gap-3 items-end">
        <div className="flex border border-slate-300 rounded-md overflow-hidden">
          {tabs.map((t) => (
            <button key={t.key} data-testid={t.testid} onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-xs uppercase tracking-[0.2em] font-bold transition-colors ${tab === t.key ? "bg-ps-blue text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Window</label>
          <select value={anomDays} onChange={(e) => setAnomDays(parseInt(e.target.value))} data-testid="anom-window" className="border border-slate-300 rounded-md bg-white px-2 py-2 text-sm font-mono-data">
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
            <option value={180}>180 days</option>
            <option value={365}>1 year</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Threshold</label>
          <select value={thresholdPct} onChange={(e) => setThresholdPct(parseInt(e.target.value))} data-testid="anom-threshold" className="border border-slate-300 rounded-md bg-white px-2 py-2 text-sm font-mono-data">
            <option value={20}>+20% above</option>
            <option value={30}>+30% above</option>
            <option value={50}>+50% above</option>
            <option value={100}>+100% above</option>
          </select>
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer mb-2">
          <input
            type="checkbox"
            checked={includeReviewed}
            onChange={(e) => setIncludeReviewed(e.target.checked)}
            data-testid="show-reviewed"
            className="w-4 h-4 accent-ps-blue"
          />
          Show already-reviewed
        </label>
        <div className="ml-auto flex gap-8">
          <Stat label={includeReviewed ? "Total" : "Pending review"} value={num(meta?.count || 0)} accent="text-ps-red" />
          <Stat label="Extra litres" value={num(meta?.total_extra_litres || 0)} accent="text-ps-orange" />
          <Stat label="Extra cost" value={ghs(meta?.total_extra_cost_ghs || 0)} accent="text-ps-red" />
        </div>
      </div>

      <div className="px-8 py-3 bg-sky-50/50 border-b border-slate-200 flex items-start gap-2 text-xs text-slate-700">
        <Info size={16} className="text-ps-blue shrink-0 mt-0.5" weight="duotone" />
        <div>
          Baselines use the median litres per trip from your own data &mdash; strongest signal first
          (<strong className="text-ps-deep">cargo</strong> → <strong className="text-ps-deep">client + destination</strong> → <strong className="text-ps-deep">client</strong>).
          Mark a row as <strong className="text-lime-700">Legitimate</strong> if the extra fuel was justified,
          or <strong className="text-ps-orange">Investigated</strong> when you've confirmed it warrants action — either way it drops out of the pending list.
        </div>
      </div>

      <div className="p-8">
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.2em] text-slate-600 border-b border-slate-200">
              <tr>
                <th className="px-3 py-3 text-left">Sev</th>
                <th className="px-3 py-3 text-left">Date</th>
                <th className="px-3 py-3 text-left">Truck</th>
                <th className="px-3 py-3 text-left">Driver</th>
                <th className="px-3 py-3 text-left">Client</th>
                <th className="px-3 py-3 text-left">Cargo / destination</th>
                <th className="px-3 py-3 text-right">Actual</th>
                <th className="px-3 py-3 text-right">Baseline</th>
                <th className="px-3 py-3 text-right">Variance</th>
                <th className="px-3 py-3 text-right">Extra cost</th>
                <th className="px-3 py-3 text-left">Status / Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-testid="anomalies-table-body">
              {loading && <tr><td colSpan={11} className="p-6"><div className="brand-progress w-1/3" /></td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={11} className="p-12 text-center">
                  <CheckCircle size={32} className="text-lime-600 mx-auto mb-2" weight="duotone" />
                  <div className="font-semibold text-ps-deep">All clear — nothing pending review.</div>
                  <div className="text-xs text-slate-500 mt-1">No fuel anomalies above {thresholdPct}% in the last {anomDays} days.</div>
                </td></tr>
              )}
              {rows.map((r) => {
                const sev = SEVERITY_STYLES[r.severity] || SEVERITY_STYLES.low;
                const reviewed = r.anomaly_status && r.anomaly_status !== "pending";
                const sb = reviewed ? STATUS_BADGE[r.anomaly_status] : null;
                return (
                  <tr key={r.id} className={`hover:bg-slate-50 ${reviewed ? "opacity-60" : ""}`} data-testid={`anomaly-row-${r.id}`}>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded border ${sev.badge}`}>{sev.label}</span>
                    </td>
                    <td className="px-3 py-2 font-mono-data text-xs whitespace-nowrap">{formatDate(r.date)}</td>
                    <td className="px-3 py-2 font-mono-data text-ps-blue font-semibold">{r.vehicle_number}</td>
                    <td className="px-3 py-2">{r.driver_name}</td>
                    <td className="px-3 py-2 font-semibold text-ps-deep">{r.client || "—"}</td>
                    <td className="px-3 py-2 text-xs text-slate-600 max-w-[200px] truncate" title={`${r.cargo || ""} ${r.destination || ""}`}>
                      {r.cargo || r.destination || "—"}
                    </td>
                    <td className="px-3 py-2 text-right font-mono-data tabular font-semibold text-ps-red">{num(r.actual_litres)} L</td>
                    <td className="px-3 py-2 text-right font-mono-data tabular text-slate-600">{num(r.baseline_litres)} L</td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-16 h-1.5 bg-slate-200 rounded overflow-hidden">
                          <div className={`h-full ${sev.bar}`} style={{ width: `${Math.min(100, r.variance_pct / 2)}%` }} />
                        </div>
                        <span className="font-mono-data tabular font-bold text-ps-red w-14 text-right">+{r.variance_pct}%</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono-data tabular text-ps-red font-semibold">{ghs(r.extra_cost_ghs)}</td>
                    <td className="px-3 py-2">
                      {reviewed && sb ? (
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded border ${sb.cls}`} title={r.anomaly_note}>
                            {sb.label}
                          </span>
                          <button onClick={() => reopenRow(r)} data-testid={`anomaly-reopen-${r.id}`} title="Reopen for review"
                            className="text-slate-400 hover:text-ps-blue p-1">
                            <ArrowCounterClockwise size={14} weight="bold" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-1.5">
                          <button onClick={() => openReview(r, "legitimate")} data-testid={`anomaly-legit-${r.id}`}
                            className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded border border-lime-300 bg-lime-50 text-lime-700 hover:bg-lime-100 transition-colors flex items-center gap-1">
                            <CheckCircle size={11} weight="bold" /> Legit
                          </button>
                          <button onClick={() => openReview(r, "investigated")} data-testid={`anomaly-investigate-${r.id}`}
                            className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded border border-orange-300 bg-orange-50 text-ps-orange hover:bg-orange-100 transition-colors flex items-center gap-1">
                            <Warning size={11} weight="bold" /> Flag
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {reviewModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center" onClick={() => setReviewModal(null)} data-testid="review-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-slate-200 flex items-start justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-ps-blue font-semibold">Review anomaly</div>
                <h2 className="font-display font-extrabold text-2xl tracking-tight text-ps-deep mt-1">
                  Mark as {reviewModal.status === "legitimate" ? "Legitimate" : "Investigated"}
                </h2>
              </div>
              <button onClick={() => setReviewModal(null)} className="text-slate-400 hover:text-ps-deep"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Truck</div><div className="font-mono-data text-ps-blue font-semibold">{reviewModal.row.vehicle_number}</div></div>
                  <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Driver</div><div>{reviewModal.row.driver_name}</div></div>
                  <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Client</div><div className="font-semibold">{reviewModal.row.client}</div></div>
                  <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Variance</div><div className="font-mono-data text-ps-red font-bold">+{reviewModal.row.variance_pct}% · {ghs(reviewModal.row.extra_cost_ghs)}</div></div>
                </div>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">
                  Note {reviewModal.status === "legitimate" ? "(why was this OK?)" : "(what action will you take?)"}
                </label>
                <textarea
                  value={note} onChange={(e) => setNote(e.target.value)}
                  data-testid="review-note"
                  rows={3}
                  placeholder={reviewModal.status === "legitimate"
                    ? "e.g. Long-haul delivery to Burkina, extra cargo weight, AC running…"
                    : "e.g. Driver questioned, escalate to manager, deduct from allowance…"
                  }
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div className="p-6 border-t border-slate-200 flex gap-3 bg-slate-50">
              <button onClick={() => setReviewModal(null)} className="flex-1 border border-slate-300 rounded-md py-2.5 text-sm hover:bg-white bg-white">Cancel</button>
              <button
                onClick={submitReview}
                disabled={saving}
                data-testid="review-submit"
                className={`flex-1 rounded-md py-2.5 text-sm font-semibold text-white transition-colors disabled:opacity-50 ${reviewModal.status === "legitimate" ? "bg-lime-600 hover:bg-lime-700" : "bg-ps-orange hover:bg-orange-700"}`}
              >
                {saving ? "Saving…" : `Confirm ${reviewModal.status === "legitimate" ? "Legitimate" : "Investigated"}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ActiveTrucks({ rows, loading, days, setDays, tabs, tab, setTab }) {
  const total = rows.length;
  const totalRevenue = rows.reduce((s, r) => s + (r.revenue_ghs || 0), 0);
  const totalLitres = rows.reduce((s, r) => s + (r.litres || 0), 0);

  return (
    <div data-testid="reports-page">
      <PageHeader subtitle="Analytics" title="Reports" />

      <div className="px-8 py-4 bg-slate-100 border-b border-slate-200 flex flex-wrap gap-3 items-end">
        <div className="flex border border-slate-300 rounded-md overflow-hidden">
          {tabs.map((t) => (
            <button
              key={t.key}
              data-testid={t.testid}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-xs uppercase tracking-[0.2em] font-bold transition-colors ${tab === t.key ? "bg-ps-blue text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Activity window</label>
          <select value={days} onChange={(e) => setDays(parseInt(e.target.value))} data-testid="active-window" className="border border-slate-300 rounded-md bg-white px-2 py-2 text-sm font-mono-data">
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </select>
        </div>
        <div className="ml-auto flex gap-8">
          <Stat label="Active trucks" value={num(total)} accent="text-ps-blue" />
          <Stat label="Total litres" value={num(totalLitres)} accent="text-ps-deep" />
          <Stat label="Revenue" value={ghs(totalRevenue)} accent="text-lime-700" />
        </div>
      </div>

      <div className="p-8">
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.2em] text-slate-600 border-b border-slate-200">
              <tr>
                <th className="px-3 py-3 text-left">Truck</th>
                <th className="px-3 py-3 text-right">Trips</th>
                <th className="px-3 py-3 text-right">Litres</th>
                <th className="px-3 py-3 text-right">Revenue</th>
                <th className="px-3 py-3 text-left">Last dispatch</th>
                <th className="px-3 py-3 text-left">Drivers</th>
                <th className="px-3 py-3 text-left">Clients served</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-testid="active-trucks-table-body">
              {loading && <tr><td colSpan={7} className="p-6"><div className="brand-progress w-1/3" /></td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-500">No truck activity in this window.</td></tr>}
              {rows.map((r) => (
                <tr key={r.vehicle_number} className="hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono-data text-ps-blue font-semibold">{r.vehicle_number}</td>
                  <td className="px-3 py-2 text-right font-mono-data tabular">{r.trips}</td>
                  <td className="px-3 py-2 text-right font-mono-data tabular">{num(r.litres)}</td>
                  <td className="px-3 py-2 text-right font-mono-data tabular text-lime-700 font-semibold">{ghs(r.revenue_ghs)}</td>
                  <td className="px-3 py-2 font-mono-data text-xs text-slate-600">{formatDate(r.last_dispatch)}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {(r.drivers || []).slice(0, 4).map((d) => (
                        <span key={d} className="px-1.5 py-0.5 text-[10px] rounded bg-slate-100 text-slate-700 font-semibold">{d}</span>
                      ))}
                      {(r.drivers || []).length > 4 && <span className="text-[10px] text-slate-500">+{r.drivers.length - 4}</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {(r.clients || []).slice(0, 4).map((c) => (
                        <span key={c} className="px-1.5 py-0.5 text-[10px] rounded bg-sky-50 text-ps-blue font-semibold">{c}</span>
                      ))}
                      {(r.clients || []).length > 4 && <span className="text-[10px] text-slate-500">+{r.clients.length - 4}</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ReportTabs({ tabs, tab, setTab, month, setMonth, children }) {
  return (
    <div className="px-8 py-4 bg-slate-100 border-b border-slate-200 flex flex-wrap gap-3 items-end">
      <div className="flex border border-slate-300 rounded-md overflow-hidden">
        {tabs.map((t) => (
          <button
            key={t.key}
            data-testid={t.testid}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-xs uppercase tracking-[0.2em] font-bold transition-colors ${tab === t.key ? "bg-ps-blue text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Month filter</label>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="report-month" className="border border-slate-300 rounded-md bg-white px-2 py-2 text-sm font-mono-data" />
      </div>
      {children}
    </div>
  );
}

function Stat({ label, value, accent = "text-ps-deep" }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">{label}</div>
      <div className={`font-mono-data tabular text-xl ${accent}`}>{value}</div>
    </div>
  );
}


function ExportDispensesButton({ month }) {
  return (
    <ExportButton
      endpoint="reports/dispenses/export"
      filenameBase={`dispenses_${month || "all"}`}
      params={{ month: month || undefined }}
      testidPrefix="dispenses-export"
    />
  );
}

function CostEfficiency({ tabs, tab, setTab, month, setMonth }) {
  const [series, setSeries] = useState([]);
  const [eff, setEff] = useState([]);
  const [gpsAnalytics, setGpsAnalytics] = useState([]);
  const [gpsMonth, setGpsMonth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState(12);
  const [replayFor, setReplayFor] = useState(null);

  useEffect(() => {
    let alive = true;
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const [cpl, te, gps] = await Promise.all([
          api.get("/reports/monthly-cost-per-litre", { params: { months } }),
          api.get("/reports/truck-efficiency", { params: month ? { month } : {} }),
          api.get("/gps/analytics/monthly", { params: month ? { month } : {} }).catch(() => ({ data: { trucks: [], month: null } })),
        ]);
        if (!alive || cancelled) return;
        setSeries(cpl.data || []);
        setEff(te.data || []);
        setGpsAnalytics(gps.data?.trucks || []);
        setGpsMonth(gps.data?.month || null);
      } finally {
        if (alive && !cancelled) setLoading(false);
      }
    };
    run();
    return () => { alive = false; cancelled = true; };
  }, [months, month]);

  const latest = series[series.length - 1];
  const prev = series[series.length - 2];
  const cplDelta = latest && prev && prev.blended_cpl ? ((latest.blended_cpl - prev.blended_cpl) / prev.blended_cpl) * 100 : 0;

  const totalLitres = eff.reduce((s, r) => s + (r.litres || 0), 0);
  const totalKm = eff.reduce((s, r) => s + (r.km || 0), 0);
  const totalCost = eff.reduce((s, r) => s + (r.cost_ghs || 0), 0);
  const fleetCpl = totalLitres > 0 ? totalCost / totalLitres : 0;
  const fleetLper100 = totalKm > 0 ? (totalLitres / totalKm) * 100 : 0;

  return (
    <div data-testid="reports-page">
      <PageHeader subtitle="Analytics" title="Cost & Efficiency" />
      <ReportTabs tabs={tabs} tab={tab} setTab={setTab} month={month} setMonth={setMonth}>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Months</label>
          <select value={months} onChange={(e) => setMonths(parseInt(e.target.value, 10))} data-testid="cpl-months" className="border border-slate-300 rounded-md bg-white px-2 py-2 text-sm font-mono-data">
            {[3, 6, 12, 18, 24, 36].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div className="flex items-end">
          <ExportDispensesButton month={month} />
        </div>
        <div className="ml-auto flex flex-wrap gap-x-8 gap-y-2">
          <Stat label="Latest CPL" value={latest ? ghs(latest.blended_cpl) : "—"} accent={cplDelta > 0 ? "text-ps-red" : "text-lime-700"} />
          <Stat label="Δ vs prev mo" value={prev ? `${cplDelta >= 0 ? "+" : ""}${cplDelta.toFixed(1)}%` : "—"} accent={cplDelta > 0 ? "text-ps-red" : "text-lime-700"} />
          <Stat label="Fleet CPL (period)" value={fleetCpl ? ghs(fleetCpl) : "—"} accent="text-ps-deep" />
          <Stat label="Fleet L/100km" value={fleetLper100 ? fleetLper100.toFixed(2) : "—"} accent="text-ps-blue" />
        </div>
      </ReportTabs>

      <div className="p-8 space-y-6">
        <div className="bg-white border border-slate-200 rounded-xl p-6 card-lift">
          <div className="text-[11px] uppercase tracking-[0.2em] text-ps-blue font-semibold mb-1">Monthly cost-per-litre</div>
          <h3 className="font-display font-extrabold text-xl tracking-tight text-ps-deep mb-4">Yard vs Top-up vs Delivered (blended)</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%" minHeight={280}>
              <LineChart data={series} margin={{ left: 8, right: 16, top: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fontFamily: "JetBrains Mono", fill: "#475569" }} stroke="#CBD5E1" />
                <YAxis tick={{ fontSize: 11, fontFamily: "JetBrains Mono", fill: "#475569" }} stroke="#CBD5E1" tickFormatter={(v) => v.toFixed(1)} />
                <Tooltip contentStyle={{ border: "1px solid #CBD5E1", borderRadius: 6, fontFamily: "JetBrains Mono", fontSize: 12 }} formatter={(v) => v ? ghs(v) : "—"} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="yard_cpl"      stroke="#006f9e" strokeWidth={2} dot={{ r: 3 }} name="Yard CPL" />
                <Line type="monotone" dataKey="topup_cpl"     stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} name="Top-up CPL" />
                <Line type="monotone" dataKey="delivered_cpl" stroke="#84cc16" strokeWidth={2} dot={{ r: 3 }} name="Delivered CPL" />
                <Line type="monotone" dataKey="blended_cpl"   stroke="#0f172a" strokeWidth={3} dot={{ r: 4 }} name="Blended CPL" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="cpl-monthly-table">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-ps-blue font-semibold">Per-month breakdown</div>
              <h3 className="font-display font-extrabold text-xl tracking-tight text-ps-deep">Cost per litre · {months} months</h3>
            </div>
          </div>
          <div className="overflow-auto max-h-[28rem]">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.2em] text-slate-600 border-b border-slate-200 sticky top-0">
                <tr>
                  <th className="px-3 py-3 text-left">Month</th>
                  <th className="px-3 py-3 text-right">Yard L</th>
                  <th className="px-3 py-3 text-right">Yard CPL</th>
                  <th className="px-3 py-3 text-right">Top-up L</th>
                  <th className="px-3 py-3 text-right">Top-up CPL</th>
                  <th className="px-3 py-3 text-right">Delivered L</th>
                  <th className="px-3 py-3 text-right">Delivered CPL</th>
                  <th className="px-3 py-3 text-right">Blended CPL</th>
                  <th className="px-3 py-3 text-right">Trips</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && <tr><td colSpan={9} className="p-6"><div className="brand-progress w-1/3" /></td></tr>}
                {!loading && series.length === 0 && <tr><td colSpan={9} className="p-8 text-center text-slate-500">No data.</td></tr>}
                {series.map((r) => (
                  <tr key={r.month} className="hover:bg-slate-50" data-testid={`cpl-row-${r.month}`}>
                    <td className="px-3 py-2 font-semibold text-ps-deep font-mono-data">{r.month}</td>
                    <td className="px-3 py-2 text-right font-mono-data tabular text-ps-blue">{num(r.yard_litres)}</td>
                    <td className="px-3 py-2 text-right font-mono-data tabular">{r.yard_cpl ? ghs(r.yard_cpl) : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono-data tabular text-ps-orange">{r.topup_litres ? num(r.topup_litres) : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono-data tabular">{r.topup_cpl ? ghs(r.topup_cpl) : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono-data tabular">{r.delivered_litres ? num(r.delivered_litres) : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono-data tabular">{r.delivered_cpl ? ghs(r.delivered_cpl) : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono-data tabular font-bold text-ps-deep">{r.blended_cpl ? ghs(r.blended_cpl) : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono-data tabular">{r.trips || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="truck-efficiency-table">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-3">
            <Gauge size={18} className="text-ps-blue" />
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-[0.2em] text-ps-blue font-semibold">Per-truck efficiency {month && `· ${month}`}</div>
              <h3 className="font-display font-extrabold text-xl tracking-tight text-ps-deep">L/100km · km/L · GHS/km</h3>
            </div>
            <div className="text-[11px] text-slate-500 max-w-xs text-right">Distance is computed from dispense `mileage` readings (odometer). If odometer isn&apos;t being logged, the L/100km columns will read 0.</div>
          </div>
          <div className="overflow-auto max-h-[32rem]">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.2em] text-slate-600 border-b border-slate-200 sticky top-0">
                <tr>
                  <th className="px-3 py-3 text-left">Truck</th>
                  <th className="px-3 py-3 text-right">Trips</th>
                  <th className="px-3 py-3 text-right">Litres</th>
                  <th className="px-3 py-3 text-right">Km</th>
                  <th className="px-3 py-3 text-right">L / 100km</th>
                  <th className="px-3 py-3 text-right">Km / L</th>
                  <th className="px-3 py-3 text-right">GHS / km</th>
                  <th className="px-3 py-3 text-right">GHS / L</th>
                  <th className="px-3 py-3 text-right">Total cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && <tr><td colSpan={9} className="p-6"><div className="brand-progress w-1/3" /></td></tr>}
                {!loading && eff.length === 0 && <tr><td colSpan={9} className="p-8 text-center text-slate-500">No data.</td></tr>}
                {eff.map((r) => (
                  <tr key={r.vehicle_number} className="hover:bg-slate-50" data-testid={`eff-row-${r.vehicle_number}`}>
                    <td className="px-3 py-2 font-mono-data text-ps-blue font-bold">{r.vehicle_number}</td>
                    <td className="px-3 py-2 text-right font-mono-data tabular">{r.trips}</td>
                    <td className="px-3 py-2 text-right font-mono-data tabular text-ps-blue">{num(r.litres)}</td>
                    <td className="px-3 py-2 text-right font-mono-data tabular">{r.km ? num(r.km) : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono-data tabular font-bold">{r.l_per_100km ? r.l_per_100km.toFixed(2) : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono-data tabular">{r.km_per_l ? r.km_per_l.toFixed(2) : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono-data tabular">{r.ghs_per_km ? ghs(r.ghs_per_km) : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono-data tabular">{ghs(r.ghs_per_l)}</td>
                    <td className="px-3 py-2 text-right font-mono-data tabular font-semibold text-lime-700">{ghs(r.cost_ghs)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Fleet Analytics (GPS-derived) ── */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="fleet-analytics-table">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-3 flex-wrap">
            <Gauge size={18} className="text-lime-600" />
            <div className="flex-1 min-w-[260px]">
              <div className="text-[11px] uppercase tracking-[0.2em] text-lime-700 font-semibold">Fleet analytics · GPS-derived {gpsMonth && `· ${gpsMonth}`}</div>
              <h3 className="font-display font-extrabold text-xl tracking-tight text-ps-deep">Distance · idle time · cross-border residency</h3>
            </div>
            <div className="text-[11px] text-slate-500 max-w-sm text-right">
              Computed from <code className="font-mono-data">gps_history</code> snapshots polled every minute. Distance via Haversine, idle = engine on &lt; 2 km/h, country residency from address geocoding.
            </div>
          </div>
          <div className="overflow-auto max-h-[32rem]">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.2em] text-slate-600 border-b border-slate-200 sticky top-0">
                <tr>
                  <th className="px-3 py-3 text-left">Truck</th>
                  <th className="px-3 py-3 text-right">Snapshots</th>
                  <th className="px-3 py-3 text-right">Distance (km)</th>
                  <th className="px-3 py-3 text-right">Engine hrs</th>
                  <th className="px-3 py-3 text-right">Idle hrs</th>
                  <th className="px-3 py-3 text-right">Fuel Δ %</th>
                  <th className="px-3 py-3 text-right">km / fuel %</th>
                  <th className="px-3 py-3 text-left">Time per country</th>
                  <th className="px-3 py-3 text-right">Replay</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && <tr><td colSpan={9} className="p-6"><div className="brand-progress w-1/3" /></td></tr>}
                {!loading && gpsAnalytics.length === 0 && (
                  <tr><td colSpan={9} className="p-8 text-center text-slate-500">No GPS history for this month yet. The background poller runs every 60 s — snapshots will accumulate over time.</td></tr>
                )}
                {gpsAnalytics.map((r) => (
                  <tr key={r.vehicle_number} className="hover:bg-slate-50" data-testid={`fleet-analytics-row-${r.vehicle_number}`}>
                    <td className="px-3 py-2 font-mono-data text-ps-blue font-bold">{r.vehicle_number}</td>
                    <td className="px-3 py-2 text-right font-mono-data tabular text-slate-500">{num(r.snapshots)}</td>
                    <td className="px-3 py-2 text-right font-mono-data tabular font-semibold text-ps-deep">{num(r.distance_km)}</td>
                    <td className="px-3 py-2 text-right font-mono-data tabular">{r.hours_engine_on ? r.hours_engine_on.toFixed(1) : "—"}</td>
                    <td className={`px-3 py-2 text-right font-mono-data tabular ${r.idle_hours > 4 ? "text-amber-700 font-bold" : ""}`}>{r.idle_hours ? r.idle_hours.toFixed(1) : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono-data tabular text-ps-orange">{r.fuel_pct_drop != null ? `${r.fuel_pct_drop}%` : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono-data tabular">{r.km_per_fuel_pct != null ? r.km_per_fuel_pct.toFixed(2) : "—"}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(r.time_by_country_h || {}).sort((a, b) => b[1] - a[1]).map(([cc, h]) => (
                          <span key={cc} className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono-data rounded border border-slate-200 bg-slate-50 text-slate-700">
                            <b>{cc}</b>
                            <span className="text-slate-500">{h.toFixed(1)}h</span>
                          </span>
                        ))}
                        {Object.keys(r.time_by_country_h || {}).length === 0 && <span className="text-slate-400 text-xs">—</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => setReplayFor(r.vehicle_number)}
                        data-testid={`fleet-analytics-replay-${r.vehicle_number}`}
                        className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded bg-slate-100 text-slate-600 hover:bg-ps-blue hover:text-white transition-colors"
                      >
                        Replay
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {replayFor && <RouteReplayModal vehicleNumber={replayFor} onClose={() => setReplayFor(null)} />}
    </div>
  );
}
